/**
 * Секция «Аудит» экрана «Администратор» (задача 37).
 *
 * Показывает журнал действий: «кто, когда и что изменил». Журнал за сутки приходит вместе
 * с данными экрана (`GET /api/admin/screen`, задача 38, этап C), поэтому открытие раздела
 * не делает отдельного запроса, а время секции не прибавляется к времени экрана. Период
 * переключается дропдауном («За день» по умолчанию — последние 24 часа, «За неделю» —
 * 7 суток): тогда уходит запрос `GET /api/admin/audit?period=…`, а таблица
 * перерисовывается без перезагрузки страницы.
 *
 * Повторные показы периодов обслуживают память браузера (`adminAuditCache`) и версия
 * ответа (`ETag`): свежий журнал берётся из памяти, устаревший перепроверяется условным
 * запросом и при неизменных записях приходит `304` без тела. Кнопка «Обновить» память
 * игнорирует — журнал не обновляется сам: события пишут другие люди и вкладки, а таблица
 * показывает срез на момент запроса.
 *
 * Записи идут свежими сверху; окно и общее число записей подписаны над таблицей, чтобы
 * усечение по лимиту (`AUDIT_SCREEN_LIMIT`) было видно, а не выглядело как «столько и
 * было». Секция только читает: правок и кнопок сохранения у неё нет. Ошибку чтения
 * журнала сервер отдаёт как 502 (или `audit: null` в ответе экрана) — экран показывает её
 * сообщением: пустая таблица здесь означала бы «ничего не происходило», а это неправда.
 */
import {
  AUDIT_PERIODS,
  AUDIT_UNAVAILABLE_MESSAGE,
  type AuditPeriod,
  type AuditScreen,
} from '../shared/audit';
import * as api from './api';
import { ApiError } from './api';
import { auditScreenEtag, cachedAuditScreen, rememberAuditScreen } from './adminAuditCache';
import { button, el, messageBox } from './dom';
import { cell, errorText, sectionFrame, selectControl, tableHead } from './ui';
import { auditPeriodTitle, auditRangeText, auditTotalText, formatAuditTime } from './auditView';

/** Контекст экрана: общий сбой (истёкшая сессия). */
interface ScreenContext {
  fail(err: unknown): void;
}

/** Секция «Аудит»: каркас для списка секций и показ журнала, пришедшего с экраном. */
export interface AuditSection {
  /** Каркас секции: вставляется последним в список секций экрана. */
  root: HTMLElement;
  /**
   * Показывает журнал, пришедший вместе с данными экрана (задача 38, этап C).
   * `null` — журнал прочитать не удалось: секция сообщает об этом, а раздел работает.
   */
  showFromScreen(audit: AuditScreen | null): void;
}

/** Подписи колонок таблицы журнала: порядок как на экране. */
const AUDIT_COLUMNS = ['Дата и время', 'Событие'];

/** Строка таблицы журнала: время моноширинным блоком, описание — текстом. */
function auditRow(entry: AuditScreen['entries'][number]): HTMLTableRowElement {
  return el('tr', {
    children: [
      cell(
        AUDIT_COLUMNS[0],
        el('time', { text: formatAuditTime(entry.time), attrs: { datetime: entry.time } }),
      ),
      cell(AUDIT_COLUMNS[1], el('span', { text: entry.description })),
    ],
  });
}

/** Таблица журнала или пояснение, что записей за период нет. */
function auditTable(screen: AuditScreen): HTMLElement {
  if (screen.entries.length === 0) {
    return el('p', { className: 'section__hint', text: auditTotalText(screen) });
  }
  return el('table', {
    className: 'table table--audit',
    children: [
      tableHead(AUDIT_COLUMNS),
      el('tbody', { children: screen.entries.map((entry) => auditRow(entry)) }),
    ],
  });
}

/**
 * Секция «Аудит»: дропдаун периода, кнопка «Обновить», подпись окна и таблица записей.
 *
 * Кнопка «Обновить» нужна потому, что журнал не обновляется сам: события пишут другие
 * люди и вкладки, а таблица показывает срез на момент запроса. Перезагружать страницу
 * ради свежих строк не нужно — повторный запрос того же периода идёт по кнопке (память
 * браузера она игнорирует). Пока ответ в пути, кнопка выключена: двойной клик не должен
 * слать второй запрос.
 *
 * `sectionClass: 'section--audit'` даёт телу секции верхний отступ: шапка секции
 * залипает у кромки окна (`.section__summary`), и панель с дропдауном без отступа
 * читалась как приклеенная к ней.
 */
export function auditSection(context: ScreenContext): AuditSection {
  const body = el('div', { className: 'section__body' });
  const { root } = sectionFrame('Аудит', body, { sectionClass: 'section--audit' });
  const message = messageBox();
  const tableHost = el('div');

  const periodSelect = selectControl(
    AUDIT_PERIODS.map(({ value, label }) => ({ value, label })),
    AUDIT_PERIODS[0].value,
  );
  periodSelect.id = 'audit-period';

  const rangeText = el('p', { className: 'section__hint' });
  const refreshButton = button({
    text: 'Обновить',
    onClick: () => void load({ force: true }),
  });
  const toolbar = el('div', {
    className: 'toolbar',
    children: [
      el('label', { className: 'field__label', attrs: { for: 'audit-period' }, text: 'Период' }),
      periodSelect,
      refreshButton,
    ],
  });

  /**
   * Номер запроса: пока ответ в пути, пользователь может переключить период — тогда
   * результат устаревшего запроса отбрасывается и таблица не мигает старым окном.
   */
  let requestNumber = 0;

  /**
   * Показывает журнал периода: подпись окна, таблица и память браузера.
   * `etag` — версия ответа: следующий запрос того же периода станет условным.
   */
  function render(screen: AuditScreen, etag?: string): void {
    rememberAuditScreen(screen.period, screen, etag);
    message.clear();
    rangeText.textContent =
      `${auditPeriodTitle(screen.period)}: ${auditRangeText(screen)}. ` + auditTotalText(screen);
    tableHost.replaceChildren(auditTable(screen));
  }

  /**
   * Загружает журнал выбранного периода и перерисовывает таблицу. `force` — по кнопке
   * «Обновить»: память браузера игнорируется, данные запрашиваются заново (условный
   * запрос всё равно ответит `304`, если записи не изменились).
   */
  async function load(options: { force?: boolean } = {}): Promise<void> {
    const period = periodSelect.value as AuditPeriod;
    const request = (requestNumber += 1);
    const remembered = cachedAuditScreen(period);
    if (remembered && !options.force) {
      render(remembered, auditScreenEtag(period));
      return;
    }

    // Кнопка выключена, пока ответ в пути; включает её только последний запрос:
    // устаревший ответ не должен включать кнопку раньше свежего.
    refreshButton.disabled = true;
    message.show('Загружаем журнал…', 'info');

    try {
      const response = await api.fetchAdminAudit(period, auditScreenEtag(period));
      if (request !== requestNumber) return;
      // `304` — версия не изменилась: таблица остаётся на месте, срок памяти продлевается.
      if (response.notModified) {
        if (remembered) render(remembered, auditScreenEtag(period));
        else message.clear();
        return;
      }
      if (response.data) render(response.data, response.etag);
    } catch (err) {
      if (request !== requestNumber) return;
      // Истёкшая сессия уводит на вход; сбой чтения журнала остаётся на экране
      // сообщением — раздел администратора при этом работает.
      if (err instanceof ApiError && err.status === 401) {
        context.fail(err);
        return;
      }
      message.clear();
      tableHost.replaceChildren();
      message.show(errorText(err), 'error');
    } finally {
      if (request === requestNumber) refreshButton.disabled = false;
    }
  }

  /** Журнал «за день» приходит с данными экрана: отдельного запроса при открытии нет. */
  function showFromScreen(audit: AuditScreen | null): void {
    if (audit) {
      // Период могли переключить, пока экран грузился: тогда его данные покажет запрос
      // переключения, а не этот ответ.
      if (audit.period === periodSelect.value) render(audit);
      return;
    }
    // Журнал не прочитан: пустая таблица означала бы «ничего не происходило».
    message.show(AUDIT_UNAVAILABLE_MESSAGE, 'error');
    tableHost.replaceChildren();
  }

  periodSelect.addEventListener('change', () => void load());
  body.replaceChildren(toolbar, rangeText, message.root, tableHost);
  return { root, showFromScreen };
}
